import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Mail, MessageSquare, PhoneCall } from "lucide-react";

import { MarketingFooter } from "@/components/layout/MarketingFooter";

const contactCards = [
  {
    title: "Talk to sales",
    body: "Discuss rollout plans, pricing, and whether theRecruit fits your hiring workflow.",
    href: "mailto:apex25@theresume.io?subject=Sales Inquiry",
    cta: "Email sales",
    icon: Mail,
  },
  {
    title: "See the workflow live",
    body: "Book a demo focused on sourcing, candidate evidence, and recruiter decision speed.",
    href: "mailto:apex25@theresume.io?subject=Demo Request",
    cta: "Request demo",
    icon: CalendarDays,
  },
  {
    title: "Need support",
    body: "Questions about onboarding, current plan usage, or feature behavior? Reach the team directly.",
    href: "mailto:apex25@theresume.io?subject=Support Request",
    cta: "Contact support",
    icon: MessageSquare,
  },
];

const helpTopics = [
  "Evaluate sourcing and ranking workflows",
  "Plan recruiter or hiring-manager rollout",
  "Review fit against your current ATS process",
  "Answer pricing, security, and onboarding questions",
];

export const ContactPage = () => {
  return (
    <div className="technical-home relative min-h-screen antialiased overflow-x-hidden">
      <section className="relative pt-28 pb-20">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-10 lg:grid-cols-12 lg:items-end"
          >
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em]">
                <PhoneCall className="h-3.5 w-3.5" />
                Contact theRecruit
              </span>
              <h1 className="mt-8 font-display text-5xl leading-[0.95] tracking-tight md:text-7xl">
                Bring more evidence into every hiring decision.
              </h1>
            </div>
            <p className="max-w-xl text-lg leading-relaxed lg:col-span-4">
              Whether you are evaluating the platform, planning rollout, or tightening an existing
              workflow, we can help you move from candidate volume to candidate clarity.
            </p>
          </motion.div>

          <div className="mt-14 grid gap-px border bg-[var(--tr-tech-line)] lg:grid-cols-3">
            {contactCards.map((card, index) => (
              <motion.a
                key={card.title}
                href={card.href}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.34, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-[var(--tr-tech-panel)] p-7 transition-colors hover:bg-[var(--signal-soft)]"
              >
                <div className="flex h-12 w-12 items-center justify-center border">
                  <card.icon className="h-5 w-5" />
                </div>
                <h2 className="mt-6 font-display text-3xl leading-tight">{card.title}</h2>
                <p className="mt-4 leading-relaxed">{card.body}</p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em]">
                  {card.cta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid gap-px border bg-[var(--tr-tech-line)] lg:grid-cols-[1.2fr_0.8fr]">
            <div className="technical-dark-section bg-[#1b1c19] p-8 md:p-10">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em]">What we help with</p>
              <h2 className="mt-5 font-display text-4xl leading-tight">
                Practical conversations, not generic sales scripts.
              </h2>
              <div className="mt-8 grid gap-px bg-[rgba(250,249,244,0.18)] md:grid-cols-2">
                {helpTopics.map((item) => (
                  <div key={item} className="bg-[#1b1c19] p-5">
                    <div className="mb-4 h-2 w-2 bg-[#d4a853]" />
                    <p className="text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[var(--tr-tech-panel)] p-8">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em]">
                Response expectations
              </p>
              <div className="mt-6 space-y-7">
                <div>
                  <h3 className="font-display text-2xl">Sales and demos</h3>
                  <p className="mt-2 leading-relaxed">
                    Best for evaluating fit, pricing, rollout sequencing, and team adoption.
                  </p>
                </div>
                <div>
                  <h3 className="font-display text-2xl">Support</h3>
                  <p className="mt-2 leading-relaxed">
                    Best for active customer questions about plans, behavior, or workflow issues.
                  </p>
                </div>
                <div className="border bg-[var(--signal-soft)] p-5">
                  <p className="text-sm font-semibold">Recommended first step</p>
                  <p className="mt-2 text-sm leading-relaxed">
                    If you are evaluating the platform, start with sales. If you already use the
                    product, contact support with workflow and plan context.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ContactPage;
